// Sidebar navigation for control panel
// Requirements: 5.1, 5.2, 5.3

import React from 'react';
import { FeatureModule } from './ControlPanel';
import { User } from '../../types/auth';
import OptinodeAILogo from '../common/OptinodeAILogo';
import { BuildingIcon, PackageIcon, ZapIcon, AnalyticsIcon, DashboardIcon } from '../common/icons';
import './SidebarNavigation.css';

interface NavigationItem {
  id: FeatureModule;
  label: string;
  icon: React.ComponentType<{ width?: number; height?: number; color?: string; className?: string }>;
  description: string;
  ariaLabel: string;
}

interface SidebarNavigationProps {
  activeModule: FeatureModule;
  onModuleChange: (module: FeatureModule) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  user: User;
}

export const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  activeModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse,
  user
}) => {
  const navigationItems: NavigationItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: DashboardIcon,
      description: 'Supply chain health and performance overview',
      ariaLabel: 'Navigate to dashboard overview'
    },
    {
      id: 'network',
      label: 'Manage Network',
      icon: BuildingIcon,
      description: 'Configure warehouse locations and distribution network',
      ariaLabel: 'Navigate to warehouse network management'
    },
    {
      id: 'inventory',
      label: 'Manage Inventory',
      icon: PackageIcon,
      description: 'Track inventory levels and demand patterns',
      ariaLabel: 'Navigate to inventory management'
    },
    {
      id: 'optimize',
      label: 'Optimize Inventory',
      icon: ZapIcon,
      description: 'Run optimization algorithms for allocation',
      ariaLabel: 'Navigate to optimization engine'
    },
    {
      id: 'planning',
      label: 'Planning',
      icon: AnalyticsIcon,
      description: 'S&OP scenario planning and analysis',
      ariaLabel: 'Navigate to S&OP planning'
    }
  ];

  const handleItemClick = (module: FeatureModule) => {
    onModuleChange(module);
  };

  const handleKeyDown = (event: React.KeyboardEvent, module: FeatureModule) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleItemClick(module);
    }
  };

  return (
    <aside 
      className={`sidebar-navigation ${isCollapsed ? 'collapsed' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <OptinodeAILogo 
            width={isCollapsed ? 32 : 40} 
            height={isCollapsed ? 32 : 40} 
            className="sidebar-logo" 
          />
          {!isCollapsed && (
            <span className="sidebar-brand-text">Optinode AI</span>
          )}
        </div>
        
        <button
          className="sidebar-toggle"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className={`toggle-icon ${isCollapsed ? 'collapsed' : ''}`}>
            {isCollapsed ? '▶' : '◀'}
          </span>
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav">
        <ul className="nav-list" role="menubar">
          {navigationItems.map((item) => (
            <li key={item.id} className="nav-item" role="none">
              <button
                className={`nav-link ${activeModule === item.id ? 'active' : ''}`}
                onClick={() => handleItemClick(item.id)}
                onKeyDown={(e) => handleKeyDown(e, item.id)}
                aria-label={item.ariaLabel}
                aria-current={activeModule === item.id ? 'page' : undefined}
                role="menuitem"
                title={isCollapsed ? item.label : item.description}
              >
                <span className="nav-icon" aria-hidden="true">
                  <item.icon width={20} height={20} color="currentColor" />
                </span>
                {!isCollapsed && (
                  <div className="nav-content">
                    <span className="nav-label">{item.label}</span>
                    <span className="nav-description">{item.description}</span>
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Sidebar Footer */}
      {!isCollapsed && (
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <span className="avatar-text">
                {user.profile?.firstName?.[0] || user.email[0].toUpperCase()}
              </span>
            </div>
            <div className="user-details">
              <span className="user-name">
                {user.profile?.firstName && user.profile?.lastName
                  ? `${user.profile.firstName} ${user.profile.lastName}`
                  : user.email}
              </span>
              <span className="user-tenant">
                {user.tenant?.name || 'Default Tenant'}
              </span>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default SidebarNavigation;